<script>
import { SancareOcticon } from '@sancare/typescript-commons'
import _ from 'lodash'

import PredictedDiagnosis from '../PredictedDiagnosis.vue'

export default {
  components: {
    'predicted-diagnosis': PredictedDiagnosis,
    'sancare-octicon': SancareOcticon,
  },
  props: {
    appSettings: { type: Object, required: true },
    type: { type: String, required: true },
    rumId: { type: Number, required: true },
    diagnosisGroup: { type: Object, required: true },
    healthData: { type: Object, required: true },
    selection: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
    rumPredictedLabels: { type: Array, default: null },
    isProposal: { type: Boolean, default: false },
    suggestionJustifications: { type: Object, default: null },
  },
  emits: ['make-selection'],
  computed: {
    predictedDiagnoses() {
      return this.rumPredictedLabels.filter((dDiag) => dDiag.type === this.type)
    },
    currentDiagnosis() {
      return this.diagnosisGroup[`current${this.type}`] ?? null
    },
    predictedDiagnosis() {
      if (!this.currentDiagnosis) {
        return null
      }
      const currentReference = this.currentDiagnosis.reference
      const prediction = _.find(this.predictedDiagnoses, (dDiag) => dDiag.diagnosis.reference === currentReference)

      // if diag has been promoted, but is no more predicted
      if (!prediction) {
        return {
          diagnosis: { ...this.currentDiagnosis },
          hints: [],
          canShowRational: false,
        }
      }

      return {
        ...prediction,
        canShowRational: prediction.hasRational ?? _.some(prediction.hints, (h) => h.confidence >= this.appSettings['standard_mode_hint_threshold']),
      }
    },
    displayedDiagnoses() {
      const currentReference = this.currentDiagnosis?.reference ?? null
      return _.map(
        _.filter(
          _.orderBy(this.predictedDiagnoses, ['confidence', 'reference'], ['desc', 'asc']),
          (dDiag) => dDiag.diagnosis.reference !== currentReference
        ),
        (dDiag) => ({
          ...dDiag,
          // hasRational comes precomputed from the AI automation preview endpoint (no chunks/hints
          // shipped there); fall back to the legacy hints-based computation for other data sources.
          canShowRational: dDiag.hasRational ?? _.some(dDiag.hints, (h) => h.confidence >= this.appSettings['standard_mode_hint_threshold']),
        })
      )
    },
  },
  methods: {
    makeSelection(selection) {
      this.$emit('make-selection', selection)
    },
  }
}
</script>

<template>
  <div :key="rumId">
    <div class="row g-0 diagnosis-list-container">
      <div
        v-if="!predictedDiagnosis"
        key="no-diagnosis"
        class="card diagnosis-card"
        :class="[isProposal ? 'col-12' : 'col-6', { highlighted: !isProposal }]"
      >
        <div class="card-body">
          <h1 class="card-title d-flex align-items-center">
            <span>{{ type }}</span>
            <small class="ms-1 fs-6">- {{ isProposal ? $t('stayDisplayer.mco.diagnosisStatus.proposal') : $t('stayDisplayer.mco.diagnosisStatus.current') }}</small>
          </h1>
        </div>
        <div class="card-body">
          Pas de {{ type }}
        </div>
      </div>
      <h1
        v-if="!predictedDiagnosis"
        key="other-diagnoses-title"
        class="col-12 other-diagnoses-title flex-priority-1"
      >
        {{ predictedDiagnoses.length ? 'Taux de confiance des prédictions insuffisants' : 'Aucune prédiction' }}
      </h1>
      <div
        v-if="predictedDiagnosis"
        :class="isProposal ? 'col-12' : 'col-6'"
      >
        <predicted-diagnosis
          :can-show-rational="predictedDiagnosis.canShowRational"
          :predicted-diagnosis="predictedDiagnosis"
          :health-data="healthData"
          :is-loading="isLoading"
          :selected="selection != null && selection.diagType === type && selection.diagReference === predictedDiagnosis.diagnosis.reference"
          :suggestion-justifications="suggestionJustifications"
          class="card diagnosis-card"
          :class="{ highlighted: !isProposal }"
          @make-selection="makeSelection"
        >
          <div class="card-body">
            <h1 class="card-title d-flex align-items-center">
              <span>{{ type }}</span>
              <small class="ms-1 fs-6">- {{ isProposal ? $t('stayDisplayer.mco.diagnosisStatus.proposal') : $t('stayDisplayer.mco.diagnosisStatus.current') }}</small>
            </h1>
          </div>
          <div class="card-body diagnosis-title">
            <div class="row g-0 align-items-center">
              <h2
                :title="predictedDiagnosis.diagnosis.description"
                class="col me-1"
              >
                {{ predictedDiagnosis.diagnosis.reference }}
              </h2>
              <div class="col-1">
                <div
                  v-if="predictedDiagnosis.canShowRational"
                  :class="{ selected: selection != null && selection.diagType === type && selection.diagReference === predictedDiagnosis.diagnosis.reference }"
                  class="btn-select-diagnosis icon-block"
                  @click.stop="makeSelection({ diagType: type, diagReference: predictedDiagnosis.diagnosis.reference, savedSearchIds: [] })"
                >
                  <sancare-octicon
                    name="note"
                    :width="18"
                    :height="18"
                  />
                </div>
              </div>
            </div>
            <div>
              <p>
                {{ predictedDiagnosis.diagnosis.description }}
              </p>
            </div>
          </div>
        </predicted-diagnosis>
      </div>
      <div
        v-for="(predictedDiagnosis, idx) in displayedDiagnoses"
        :key="`${rumId}-${predictedDiagnosis.diagnosis.reference}`"
        class="col-6 mb-2"
        :class="{
          'ps-1': idx % 2,
          'pe-1': (idx - 1) % 2,
        }"
      >
        <predicted-diagnosis
          :can-show-rational="predictedDiagnosis.canShowRational"
          :predicted-diagnosis="predictedDiagnosis"
          :health-data="healthData"
          :is-loading="isLoading"
          :selected="selection != null && selection.diagType === type && selection.diagReference === predictedDiagnosis.diagnosis.reference"
          :suggestion-justifications="suggestionJustifications"
          class="card diagnosis-card small"
          @make-selection="makeSelection"
        >
          <div
            class="card-body diagnosis-title"
          >
            <div class="row g-0 align-items-center">
              <h2
                :title="predictedDiagnosis.diagnosis.description"
                class="col me-1"
              >
                {{ predictedDiagnosis.diagnosis.reference }}
              </h2>
              <div class="col-1">
                <div
                  v-if="predictedDiagnosis.canShowRational"
                  :class="{ selected: selection != null && selection.diagType === type && selection.diagReference === predictedDiagnosis.diagnosis.reference }"
                  class="btn-select-diagnosis icon-block"
                  @click.stop="makeSelection({ diagType: type, diagReference: predictedDiagnosis.diagnosis.reference, savedSearchIds: [] })"
                >
                  <sancare-octicon
                    name="note"
                    :width="18"
                    :height="18"
                  />
                </div>
              </div>
            </div>
          </div>
        </predicted-diagnosis>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card-title {
  font-size: 2rem;
}

h2 {
  font-size: 1.1rem;
}
</style>
